<template>
  <div class="flex flex-col gap-4 w-full text-slate-700">
    <!-- Header with count & refresh button -->
    <div class="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100">
      <div class="flex items-center gap-2">
        <h2 class="text-sm font-bold text-slate-800 m-0">
          Messages History
        </h2>
        <span
          v-if="messages.length > 0 && !loading"
          class="bg-blue-50 text-blue-700 border border-blue-200/80 px-2.5 py-0.5 rounded-full text-xs font-bold"
        >
          {{ messages.length }} total
        </span>
      </div>

      <div class="flex items-center gap-2">
        <button
          @click="refreshMessages"
          :disabled="loading"
          class="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
        >
          <i class="fa-solid fa-arrows-rotate text-xs" :class="{ 'animate-spin': loading }"></i>
          <span>{{ loading ? 'Loading...' : 'Refresh' }}</span>
        </button>

        <button
          v-if="messages?.length > 0"
          :disabled="loading"
          @click="deleteAllMessageFromPlayer(user_id)"
          class="px-3.5 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200/80 font-semibold rounded-xl text-xs flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
        >
          <i class="fa-solid fa-trash-can text-xs"></i>
          <span>Delete All</span>
        </button>
      </div>
    </div>

    <!-- Skeleton loader -->
    <div v-if="loading" class="flex flex-col gap-3">
      <div v-for="n in 3" :key="n" class="flex items-center gap-3 p-4 bg-slate-50 border border-slate-100 rounded-2xl animate-pulse">
        <div class="w-10 h-10 rounded-xl bg-slate-200"></div>
        <div class="flex-1 flex flex-col gap-2">
          <div class="w-32 h-3.5 bg-slate-200 rounded"></div>
          <div class="w-3/4 h-3 bg-slate-200 rounded"></div>
        </div>
      </div>
    </div>

    <!-- Messages List -->
    <div v-else-if="!loading && messages.length > 0" class="flex flex-col gap-3">
      <div
        v-for="message in messages"
        :key="message._id"
        class="bg-slate-50/80 border border-slate-200/80 hover:border-slate-300 rounded-2xl p-4 flex items-start gap-3.5 transition-all"
      >
        <!-- Avatar -->
        <div class="shrink-0">
          <img
            v-if="message.avatar && message.avatar.startsWith('http')"
            :src="message.avatar"
            :alt="message.username || 'User'"
            class="w-10 h-10 rounded-xl object-cover border border-slate-200 bg-white"
          />
          <div
            v-else
            class="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 font-bold text-sm flex items-center justify-center border border-blue-200 shadow-xs"
          >
            {{ getAvatarInitials(message.username || message.user_id) }}
          </div>
        </div>

        <!-- Message Body -->
        <div class="flex-1 flex flex-col min-w-0">
          <div class="flex items-center justify-between gap-2">
            <span class="text-xs font-bold text-slate-800 truncate">
              {{ message.username || message.user_id }}
            </span>
            <div class="flex items-center gap-2 shrink-0">
              <span class="text-[11px] text-slate-400 font-medium">
                {{ formatDate(message.created_at) }}
              </span>
              <button
                class="w-6 h-6 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition-colors cursor-pointer"
                @click="deleteMessage(message._id)"
                title="Delete message"
              >
                <i class="fa-solid fa-trash-can text-xs"></i>
              </button>
            </div>
          </div>

          <!-- Text message -->
          <div
            v-if="message.message && message.message.trim()"
            class="text-sm text-slate-700 mt-1 break-words font-normal"
          >
            {{ message.message }}
          </div>

          <!-- Photo Media -->
          <div
            v-if="message.url && message.type === 'photo'"
            class="mt-2.5 max-w-sm rounded-xl overflow-hidden border border-slate-200 shadow-xs"
          >
            <img
              :src="message.url"
              :alt="message.message || 'Media message'"
              class="max-h-60 w-auto object-cover"
              @error="handleImageError"
            />
          </div>

          <!-- Meta tags -->
          <div class="flex items-center gap-2 mt-2">
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-200/70 text-slate-600">
              {{ message.type === "photo" ? "📷" : "💬" }}
              {{ message.type }}
            </span>
            <span
              v-if="message.message_filtered"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200/60"
            >
              🔒 Filtered
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="py-16 flex flex-col items-center justify-center text-center gap-2">
      <div class="w-14 h-14 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center text-2xl mb-1">
        <i class="fa-solid fa-comment-slash"></i>
      </div>
      <h3 class="text-sm font-bold text-slate-800 m-0">No messages yet</h3>
      <p class="text-xs text-slate-400 m-0 max-w-sm">This player has not sent any chat messages in the game.</p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getFetch, deleteRequest } from "@/api/axiosClient";
import Swal from "sweetalert2";

export default {
  name: "PlayerMessageList",
  props: {
    user_id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      messages: [],
      loading: false,
      error: null,
    };
  },
  computed: {
    ...mapGetters(["isMenuOpen"]),
  },
  mounted() {
    this.getMessage();
  },
  methods: {
    async getMessage() {
      this.loading = true;
      this.error = null;

      try {
        const params = {
          user_id: this.user_id,
        };
        const data = await getFetch("messages/", params);

        this.messages = (data.data || []).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
      } catch (error) {
        console.error("Error fetching messages:", error);
        this.error = "Failed to load messages. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    async refreshMessages() {
      await this.getMessage();
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      const day = String(date.getUTCDate()).padStart(2, "0");
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const year = date.getUTCFullYear();
      const hours = String(date.getUTCHours()).padStart(2, "0");
      const minutes = String(date.getUTCMinutes()).padStart(2, "0");
      const seconds = String(date.getUTCSeconds()).padStart(2, "0");

      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds} UTC`;
    },
    getAvatarInitials(name) {
      if (!name) return "?";
      return name.charAt(0).toUpperCase();
    },

    handleImageError(event) {
      event.target.style.display = "none";
    },
    async deleteAllMessageFromPlayer(user_id) {
      const result = await Swal.fire({
        title: this.$t("warm.messageDeleteTitle"),
        text: this.$t("warm.messageDeleteText"),
        icon: "warning",
        heightAuto: false,
        showCancelButton: true,
        confirmButtonText: this.$t("warm.messageDeleteConfirm"),
        cancelButtonText: this.$t("util.cancel"),
      });

      if (result.isConfirmed) {
        try {
          await deleteRequest(`messages/deleteallmessage/${user_id}`);
          await Swal.fire(this.$t("warm.deleted"), this.$t("warm.deletedText"));
        } catch (error) {
          console.error("Error deleting messages:", error);
          await Swal.fire(
            this.$t("warm.error"),
            this.$t("warm.messageDeletedError"),
            "error"
          );
        } finally {
          this.getMessage();
        }
      }
    },
    async deleteMessage(id) {
      const result = await Swal.fire({
        title: this.$t("warm.messageDeleteTitle"),
        text: this.$t("warm.messageDeleteText"),
        icon: "warning",
        heightAuto: false,
        showCancelButton: true,
        confirmButtonText: this.$t("warm.messageDeleteConfirm"),
        cancelButtonText: this.$t("util.cancel"),
      });

      if (result.isConfirmed) {
        try {
          await deleteRequest(`messages/${id}`);
          await Swal.fire(this.$t("warm.deleted"), this.$t("warm.deletedText"));
        } catch (error) {
          console.error("Error deleting message:", error);
          await Swal.fire(
            this.$t("warm.error"),
            this.$t("warm.messageDeletedError"),
            "error"
          );
        } finally {
          this.getMessage();
        }
      }
    },
  },
};
</script>
